import type {
  AngleStyle,
  BaseJsxgraphDisplayConfig,
  BehaviorConfig,
  CanvasConfig,
  CircleStyle,
  CoordinateSystemConfig,
  ElementLabelConfig,
  LineStyle,
  PointStyle,
  PolygonStyle,
  ReactionGroup,
  WidgetConfig,
} from '@examdojo/core/widget';
import type { TriangleElement } from './elements/shapes/triangle/triangle.model';
import type { RectangleElement } from './elements/shapes/rectangle/rectangle.model';
import type { SquareElement } from './elements/shapes/square/square.model';
import type { CircleElement } from './elements/shapes/circle/circle.model';
import type { ParallelogramElement } from './elements/shapes/parallelogram/parallelogram.model';
import type { RhombusElement } from './elements/shapes/rhombus/rhombus.model';
import type { TrapezoidElement } from './elements/shapes/trapezoid/trapezoid.model';
import type { ArcElement } from './elements/shapes/arc/arc.model';
import type { PointElement, LineElement, PolygonElement } from './elements/shapes/primitives/primitives.model';
import type { LengthAnnotationElement } from './elements/annotations/length-annotation/length-annotation.model';
import type { ArrowAnnotationElement } from './elements/annotations/arrow-annotation/arrow-annotation.model';
import type { DiagonalElement } from './elements/features/diagonal/diagonal.model';
import type { AltitudeElement } from './elements/features/altitude/altitude.model';
import type { MedianElement } from './elements/features/median/median.model';
import type { MidsegmentElement } from './elements/features/midsegment/midsegment.model';

// =============================================================================
// VALUE TYPE
// =============================================================================

/**
 * A value can be a literal number or a string expression referencing ports.
 * String expressions support basic arithmetic (+, -, *, /, sqrt) and port references.
 *
 * @example
 * 5              // literal number
 * "r1.w"         // port reference
 * "t1.c * 2"     // expression with arithmetic
 * "sqrt(r1.w * r1.w + r1.h * r1.h)"  // expression with sqrt
 */
export type Value = number | string;

/** Vertex/point location: literal or expression coordinates.
 *
 * Every position in config is expressed as `{ x, y }` where each axis is a
 * `Value` (literal number **or** string expression / port path).
 *
 * @example
 * { x: 5, y: 3 }                              // static
 * { x: "t1.point.A.x", y: "t1.point.A.y" }    // port reference
 * { x: "t1.point.A.x + 2", y: 0 }             // mixed / expression
 */
export interface PointTarget {
  x: Value;
  y: Value;
}

/** Edge alignment: reference another shape's edge, optionally flip to exterior/interior */
export interface EdgeTarget {
  ref: string;
  side?: 'exterior' | 'interior';
}

// =============================================================================
// ELEMENT TYPES
// =============================================================================

export type GeometryElementType =
  | 'Point'
  | 'Triangle'
  | 'Rectangle'
  | 'Square'
  | 'Circle'
  | 'Polygon'
  | 'Line'
  | 'Parallelogram'
  | 'Rhombus'
  | 'Trapezoid'
  | 'Arc';
export type AnnotationElementType = 'LengthAnnotation' | 'ArrowAnnotation';
export type FeatureElementType = 'Diagonal' | 'Altitude' | 'Median' | 'Midsegment';
export type AllElementType = GeometryElementType | AnnotationElementType | FeatureElementType;

// =============================================================================
// ELEMENT UNION
// =============================================================================

/** Element definition union (config surface — no id) */
export type GeometryElementDef =
  | PointElement
  | TriangleElement
  | RectangleElement
  | SquareElement
  | CircleElement
  | ParallelogramElement
  | RhombusElement
  | TrapezoidElement
  | ArcElement
  | PolygonElement
  | LineElement
  | LengthAnnotationElement
  | ArrowAnnotationElement
  | DiagonalElement
  | AltitudeElement
  | MedianElement
  | MidsegmentElement;

/** Internal element type with id injected from the Record key */
export type GeometryElement = GeometryElementDef & { id: string };

// =============================================================================
// PARAMS
// =============================================================================

export interface GeometryPadParams {
  elements: Record<string, GeometryElementDef>;
  /** Named variables controllable by sliders and referenceable in element expressions. */
  variables?: Record<string, number>;
}

// =============================================================================
// DISPLAY CONFIG
// =============================================================================

export const RIGHT_ANGLE_DISPLAY_TYPES = ['square', 'circle-dot'] as const;
export type RightAngleDisplayType = (typeof RIGHT_ANGLE_DISPLAY_TYPES)[number];

export const ANGLE_UNITS = ['deg', 'rad'] as const;
export type AngleUnit = (typeof ANGLE_UNITS)[number];

export interface GeometryShapeDisplayConfig {
  /** Enable shape edge coloring. Default: false */
  coloring?: boolean;
  /** Enable shape fill (neutral when coloring is off, colored when on). Default: true */
  fill?: boolean;
}

export interface GeometryAngleDisplayConfig {
  /** Right angle marker style. Default: 'square' */
  rightAngleDisplay?: RightAngleDisplayType;
  /** Enable angle arc coloring (fill + stroke). Default: true */
  coloring?: boolean;
  /** Enable angle arc fill. Default: true */
  fill?: boolean;
}

export const LABEL_SCOPES = ['all', 'tiered', 'given'] as const;
export type LabelScope = (typeof LABEL_SCOPES)[number];

export interface GeometryLabelDisplayConfig {
  /** Where to auto-generate element name labels. Undefined = no auto-labels. */
  names?: LabelScope;
  /** Where to auto-generate numeric value labels. Undefined = no auto-labels. */
  values?: LabelScope;
  /** Enable label coloring per shape (angle + opposite side share color). Default: true */
  coloring?: boolean;
  /** Angle unit for labels. Default: 'deg' */
  angleUnit?: AngleUnit;
  /** Unit string appended to length labels (e.g., 'cm', 'm') */
  lengthUnit?: string;
}

export interface GeometryDisplayConfig {
  shape?: GeometryShapeDisplayConfig;
  angle?: GeometryAngleDisplayConfig;
  label?: GeometryLabelDisplayConfig;
}

/** Per-element display overrides */
export interface GeometryElementDisplayConfig {
  point?: PointStyle;
  line?: LineStyle;
  polygon?: PolygonStyle;
  angle?: AngleStyle;
  circle?: CircleStyle;
  visible?: boolean;
  label?: ElementLabelConfig;
  /** Per-subpart style overrides keyed by subpart name (matches port suffixes, e.g. 'AB', 'bottom', 'A') */
  subparts?: Record<string, GeometryElementDisplayConfig>;
}

export interface GeometryPadDisplayConfig extends BaseJsxgraphDisplayConfig {
  /** Coordinate system config (default: no grid, no axes, auto bounding box) */
  coordinateSystem?: CoordinateSystemConfig;
  /** Global geometry display settings */
  geometry?: GeometryDisplayConfig;
  /** Per-element display overrides keyed by element ID or port path */
  elements?: Record<string, GeometryElementDisplayConfig>;
}

// =============================================================================
// BEHAVIOR CONFIG
// =============================================================================

export interface GeometryPadBehaviorConfig extends BehaviorConfig {
  /**
   * Port refs of elements to make interactive. Everything is fixed by default.
   * Points: "t1.point.A" | Edges: "t1.edge.AB"
   */
  interactive?: string[];
  /** Reactive condition groups that dispatch actions when conditions change */
  reactions?: ReactionGroup[];
}

// =============================================================================
// WIDGET CONFIG
// =============================================================================

export type GeometryPadWidgetConfig = WidgetConfig<
  'geometrypad',
  GeometryPadParams,
  GeometryPadDisplayConfig,
  GeometryPadBehaviorConfig
>;

// =============================================================================
// GEOMETRYPAD DEFAULTS
// =============================================================================
// All geometrypad-specific defaults in one place. These override the shared
// JSXGraph base defaults. Unlike graphpad, geometrypad is shape-focused:
// no axes, no grid, no border, and equal units (fixedAxis: 'x') by default.

/** Default geometry display settings (shape styling, angles, labels). */
export const GEOMETRY_DISPLAY_DEFAULTS = {
  shape: {
    coloring: false,
    fill: true,
  },
  angle: {
    rightAngleDisplay: 'square' as const,
    coloring: true,
    fill: true,
  },
  label: {
    coloring: true,
    angleUnit: 'deg' as const,
  },
} satisfies Required<GeometryDisplayConfig>;

/**
 * Default coordinate system config for geometrypad.
 *
 * - `axes`: hidden (shape-focused, not graph-focused)
 * - `grid`: hidden
 * - `fixedAxis`: 'x' — preserves the X range, Y adjusts to container aspect ratio
 *   to enforce equal units (1 unit on X === 1 unit on Y)
 *
 * Bounding box is auto-computed from elements when not explicitly provided.
 */
export const GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS: Required<
  Pick<CoordinateSystemConfig, 'axes' | 'grid' | 'fixedAxis'>
> = {
  axes: { visible: false },
  grid: { visible: false },
  fixedAxis: 'x',
};

/**
 * Default canvas config for geometrypad.
 *
 * - `border`: hidden (clean look for standalone shapes; graphpad defaults to visible)
 *
 * Background color is inherited from the shared base (no override).
 */
export const GEOMETRYPAD_CANVAS_DEFAULTS: Required<Pick<CanvasConfig, 'border'>> = {
  border: { visible: false },
};
